import React, { useRef, useState } from 'react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ORG_LABELS } from '../../config';
import { fmtNumber } from '../../hooks';
import { Alert, Badge, Button, Chip, Field, Select, Stat } from '../../ui/basics';
import { Drawer, useToast } from '../../ui/overlay';
import { describeHours } from './docs';

const MAX_MB = 5;
const STATUS = { new: { label: 'New', tone: 'success' }, exists: { label: 'Already exists', tone: '' }, problem: { label: 'Needs fixing', tone: 'danger' } };
const LEVEL = { Client: 'Whole client', Facility: 'Whole facility', 'Job title': 'Job title' };

/** CSV text for the problems, safe to open in a spreadsheet (a leading = + - @ would otherwise run as a formula). */
const toCsv = (rows) => '\ufeff' + rows.map((r) => r.map((v) => {
  const text = String(v ?? '');
  return `"${(/^[=+\-@\t\r]/.test(text) ? "'" + text : text).replace(/"/g, '""')}"`;
}).join(',')).join('\r\n');

function saveText(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = name;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function RefCard({ r, showOrg }) {
  const [open, setOpen] = useState(r.status === 'problem');
  const s = STATUS[r.status];
  const title = [r.client, r.facility || 'Any facility', r.job_title || 'Any job title'].join(' · ');
  return (
    <div style={{ border: '1px solid ' + (r.status === 'problem' ? 'var(--danger, #d92d20)' : 'var(--border)'), borderRadius: 8, background: 'var(--surface)' }}>
      <button type="button" className="link" aria-expanded={open} onClick={() => setOpen(!open)}
        style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', padding: '10px 12px', textAlign: 'left', color: 'inherit', textDecoration: 'none', flexWrap: 'wrap' }}>
        <b style={{ flex: 1, minWidth: 220 }}>{title}</b>
        {showOrg && <Badge>{r.organization}</Badge>}
        <Badge tone="accent">{LEVEL[r.level]}</Badge>
        <Badge>{r.documents.length} {r.documents.length === 1 ? 'document' : 'documents'}</Badge>
        <Badge tone={s.tone}>{s.label}</Badge>
      </button>
      {open && (
        <div className="stack" style={{ padding: '0 12px 12px', gap: 10 }}>
          {r.errors.length > 0 && (
            <Alert>{r.errors.map((e, i) => <div key={i}><b>Row {e.row}</b> · {e.column}: {e.message}</div>)}</Alert>
          )}
          {r.status === 'exists' && <span className="muted">This reference is already set up, so it is skipped and left unchanged.</span>}
          <div className="table-wrap"><table className="table">
            <thead><tr><th>Row</th><th>Document</th><th>Due within</th><th>Remind before due</th><th>Reminder recruiters</th></tr></thead>
            <tbody>{r.documents.map((d) => (
              <tr key={d.row}><td className="num">{d.row}</td><td><b>{d.name || '—'}</b></td>
                <td>{d.due_hours ? describeHours(d.due_hours) : '—'}</td>
                <td>{d.before_hours ? describeHours(d.before_hours) : <span className="muted">None</span>}</td>
                <td>{d.recruiters.length ? d.recruiters.map((x) => `${x.email}${x.after_hours ? ` after ${describeHours(x.after_hours)}` : ''}`).join(', ') : <span className="muted">None</span>}</td></tr>))}
            </tbody></table></div>
        </div>
      )}
    </div>
  );
}

/** Upload a spreadsheet of client references, review it, then add the good ones. */
export default function BulkUpload({ org, onClose, onSaved }) {
  const { orgs } = useAuth();
  const toast = useToast();
  const writable = orgs.filter((o) => o !== 'combined');
  const [orgKey, setOrgKey] = useState(org === 'combined' ? writable[0] : org);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const input = useRef(null);

  const form = (f) => { const data = new FormData(); data.append('organization', orgKey); data.append('file', f); return data; };
  const choose = async (f) => {
    setError(''); setPreview(null); setResult(null); setFilter('all');
    if (!f) return;
    if (!/\.(xlsx|csv)$/i.test(f.name)) { setFile(null); setError('Choose an .xlsx or .csv file.'); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setFile(null); setError(`The file is larger than ${MAX_MB} MB.`); return; }
    setFile(f); setBusy('preview');
    try { setPreview(await api.upload('Reference_import_preview/', form(f))); } catch (e) { setError(e.message); }
    setBusy('');
  };
  const changeOrg = (key) => { setOrgKey(key); setPreview(null); setResult(null); setError(''); if (file) setFile(null); if (input.current) input.current.value = ''; };
  const template = async () => {
    setError('');
    try { await api.download('Reference_import_template/', { organization: orgKey }, `client-reference-upload-template-${orgKey}.xlsx`); }
    catch (e) { setError(e.message); }
  };
  const confirm = async () => {
    setError(''); setBusy('save');
    try {
      const res = await api.upload('Reference_import_confirm/', form(file));
      setResult(res);
      toast(`${res.created} reference${res.created === 1 ? '' : 's'} added`, 'success');
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const problemFile = () => saveText('reference-upload-problems.csv', toCsv([['Row', 'Client', 'Facility', 'Job title', 'Document', 'Column', 'Problem'],
    ...preview.problems.map((p) => [p.row, p.client, p.facility, p.job_title, p.document, p.column, p.message])]));

  const s = preview ? preview.summary : null;
  const shown = preview ? preview.references.filter((r) => filter === 'all' || r.status === filter) : [];
  const orgsInFile = preview ? new Set(preview.references.map((r) => r.organization)).size : 0;

  return (
    <Drawer title="Bulk upload client references" wide onClose={onClose}
      footer={result ? <Button variant="primary" onClick={() => { onSaved(); }}>Done</Button> : <>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy === 'save'} disabled={!s || s.new === 0 || Boolean(busy)} onClick={confirm}>
          {s && s.new ? `Add ${s.new} reference${s.new === 1 ? '' : 's'}` : 'Add references'}</Button></>}>
      <div className="stack">
        {error && <Alert>{error}</Alert>}
        {!result && (
          <div className="stack" style={{ gap: 12, padding: 16, border: '1px solid var(--border)', borderRadius: 10 }}>
            <div><b>1. Get the template</b>
              <p className="muted" style={{ margin: '2px 0 0', fontSize: 13 }}>It lists every column, marks the required ones, and has an Example tab. The download for an organization lets you pick its clients and recruiters from dropdowns.</p></div>
            {org === 'combined' && (
              <Field label="Organization" hint="References are added to this organization, unless a row names another one in its Organization column.">
                <Select value={orgKey} onChange={(e) => changeOrg(e.target.value)} options={writable.map((o) => ({ value: o, label: ORG_LABELS[o] }))} aria-label="Organization" />
              </Field>)}
            <div className="row">
              <Button icon="file" onClick={template}>Download template for {ORG_LABELS[orgKey]}</Button>
              <a className="link" href={`${process.env.PUBLIC_URL || ''}/templates/client-reference-upload-template.xlsx`} download>Plain template (no dropdowns)</a>
            </div>
            <div><b>2. Upload your file</b>
              <p className="muted" style={{ margin: '2px 0 0', fontSize: 13 }}>.xlsx or .csv, up to {MAX_MB} MB. Nothing is saved until you have reviewed it below and confirmed.</p></div>
            <div className="row">
              <input ref={input} type="file" hidden accept=".xlsx,.csv" aria-label="Choose file" onChange={(e) => choose(e.target.files[0])} />
              <Button icon="upload" loading={busy === 'preview'} disabled={Boolean(busy)} onClick={() => input.current.click()}>{file ? 'Choose another file' : 'Choose file'}</Button>
              {file && <span><b>{file.name}</b> <span className="muted">({Math.max(1, Math.round(file.size / 1024))} KB)</span></span>}
            </div>
          </div>
        )}

        {result && (
          <Alert tone="info">
            <b>Done.</b> Added {fmtNumber(result.created)} reference{result.created === 1 ? '' : 's'} with {fmtNumber(result.documents)} documents.
            {result.skipped_existing > 0 && ` ${fmtNumber(result.skipped_existing)} already existed and were left as they are.`}
            {result.with_problems > 0 && ` ${fmtNumber(result.with_problems)} had problems and were not added. Fix them in the file and upload again.`}
          </Alert>
        )}

        {preview && s && (
          <>
            <div className="grid cols-4">
              <Stat label="New references" value={fmtNumber(s.new)} sub={`${fmtNumber(s.documents)} documents to add`} />
              <Stat label="Already exist" value={fmtNumber(s.exists)} sub="skipped, never changed" />
              <Stat label="Need fixing" value={fmtNumber(s.problems)} sub={`${fmtNumber(s.problem_rows)} problem${s.problem_rows === 1 ? '' : 's'} found`} />
              <Stat label="Rows read" value={fmtNumber(s.rows)} sub={`from the "${preview.sheet}" ${/csv/i.test(preview.sheet) ? 'file' : 'tab'}`} />
            </div>
            {s.problems > 0 && !result && (
              <Alert tone="warning">
                {fmtNumber(s.problems)} reference{s.problems === 1 ? ' has' : 's have'} problems and will <b>not</b> be added. The rest can be added now. Each problem names its row in your file.
                <div style={{ marginTop: 6 }}><Button size="sm" icon="file" onClick={problemFile}>Download problem list (CSV)</Button></div>
              </Alert>)}
            {s.new === 0 && s.problems === 0 && s.rows > 0 && !result && <Alert tone="info">Every reference in this file already exists, so there is nothing to add.</Alert>}
            {s.rows === 0 && <Alert tone="info">The file has a header but no data rows. Fill the Upload tab of the template.</Alert>}
            {preview.references.length > 0 && (
              <>
                <div className="row" style={{ gap: 6 }}>
                  {[['all', 'All', preview.references.length], ['new', 'New', s.new], ['exists', 'Already exist', s.exists], ['problem', 'Needs fixing', s.problems]].map(([k, label, n]) => (
                    <Chip key={k} on={filter === k} onClick={() => setFilter(k)}>{label} · {n}</Chip>))}
                </div>
                <div className="stack" style={{ gap: 8 }}>
                  {shown.map((r, i) => <RefCard key={`${filter}-${i}-${r.client}-${r.facility}-${r.job_title}`} r={r} showOrg={orgsInFile > 1} />)}
                  {shown.length === 0 && <span className="muted">Nothing in this group.</span>}
                </div>
              </>)}
          </>
        )}
      </div>
    </Drawer>
  );
}
